import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, onUnauthorised } from "./api";
import type { Alert, Board, IntentSummary, MacroScope, Scope, SessionInfo } from "./types";
import Login from "./components/Login";
import Canvas, { type ComposeRequest } from "./components/Canvas";
import TopFilters, { EMPTY_MACRO, loadMacro, storeMacro } from "./components/TopFilters";
import IntentDrawer from "./components/IntentDrawer";
import Dashboard from "./components/Dashboard";
import Users from "./components/Users";
import Upload from "./components/Upload";
import AlertConfigs from "./components/AlertConfigs";

type View = "canvas" | "alerts" | "dashboard" | "users";

const LAST_BOARD_KEY = "gentik.lastBoard";

function readLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLocal(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* storage may be blocked */ }
}

export default function App() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState<View>("canvas");
  const [boards, setBoards] = useState<{ id: string; name: string }[]>([]);
  const [board, setBoard] = useState<Board | null>(null);
  const [intents, setIntents] = useState<IntentSummary[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [openIntent, setOpenIntent] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [composeRequest, setComposeRequest] = useState<ComposeRequest | null>(null);
  const [macro, setMacro] = useState<MacroScope>(EMPTY_MACRO);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number>();

  const notify = useCallback((msg: string) => {
    setToast(msg);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);

  // ── session ─────────────────────────────────────────────────────────────
  const loadSession = useCallback(async () => {
    try {
      setSession(await api.session());
    } catch {
      setSession(null);
    } finally {
      setChecking(false);
    }
  }, []);
  useEffect(() => { loadSession(); }, [loadSession]);
  useEffect(() => onUnauthorised(() => setSession(null)), []);

  // ── boards ──────────────────────────────────────────────────────────────
  const loadBoard = useCallback(async (boardId: string) => {
    const data = await api.board(boardId);
    setBoard(data.board);
    setIntents(data.intents);
    setAlerts([]);
    api.alerts(boardId).then((d) => setAlerts(d.alerts)).catch(() => undefined);
    setMacro(loadMacro(boardId));
    writeLocal(LAST_BOARD_KEY, boardId);
  }, []);

  useEffect(() => {
    if (!session) return;
    api.boards().then(({ boards }) => {
      setBoards(boards);
      const last = readLocal(LAST_BOARD_KEY);
      const pick = boards.find((b) => b.id === last) ?? boards[0];
      if (pick) loadBoard(pick.id).catch((e) => notify(e.message));
    }).catch((e) => notify(e.message));
  }, [session, loadBoard, notify]);

  // Refresh card statuses: quickly while anything is running, slowly otherwise
  // (other planners may be adding intents to the same board).
  const anyActive = intents.some((i) => i.status === "queued" || i.status === "running");
  useEffect(() => {
    if (!board) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      api.board(board.id).then((d) => {
        setIntents(d.intents);
        setBoard((cur) => (cur && d.board.rev > cur.rev ? d.board : cur));
      }).catch(() => undefined);
      api.alerts(board.id).then((d) => setAlerts(d.alerts)).catch(() => undefined);
    }, anyActive ? 2500 : 20000);
    return () => window.clearInterval(id);
  }, [board?.id, anyActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveIntent = useCallback((id: string, pos: { dx: number; dy: number }) => {
    if (!board) return;
    const prev = intents.find((i) => i.id === id)?.pos ?? null;
    setIntents((cur) => cur.map((i) => (i.id === id ? { ...i, pos } : i))); // optimistic
    api.updateIntent(board.id, id, { pos }).catch((e) => {
      setIntents((cur) => cur.map((i) => (i.id === id ? { ...i, pos: prev } : i)));
      notify(`Couldn't save the card's position: ${(e as Error).message}`);
    });
  }, [board, intents, notify]);

  const changeMacro = useCallback((m: MacroScope) => {
    setMacro(m);
    if (board) storeMacro(board.id, m);
  }, [board]);

  const refreshIntents = useCallback(() => {
    if (board) api.board(board.id).then((d) => setIntents(d.intents)).catch(() => undefined);
  }, [board]);

  const refreshAlerts = useCallback(() => {
    if (board) api.alerts(board.id).then((d) => setAlerts(d.alerts)).catch(() => undefined);
  }, [board]);
  const attention = alerts.filter((a) => a.status === "open" || a.status === "acknowledged").length;

  const createIntent = useCallback(async (text: string, run: boolean, fork?: { parentId: string; scope: Scope },
                                         pos: { dx: number; dy: number } | null = null) => {
    if (!board) throw new Error("No workstream is open.");
    // A fork keeps the scope its question was answered for; otherwise the top filters set it.
    const scope: Scope = fork ? { ...fork.scope, skus: [...(fork.scope.skus ?? [])] }
      : { family: macro.family, group1: macro.group1, group2: macro.group2, skus: [] };
    const hint = macro.functions.length === 1 ? macro.functions[0] : "";
    const intent = await api.createIntent(board.id, text, scope, hint, fork?.parentId ?? "", pos);
    setIntents((cur) => [...cur, intent]);
    if (run) {
      try {
        await api.runIntent(board.id, intent.id);
        notify(`Running ${intent.id} against “${session?.config.scenario}”.`);
      } catch (e) {
        notify(`Saved ${intent.id}, but it didn't start: ${(e as Error).message}`);
      }
    }
    // No immediate reload: it could race the card's position save. Polling picks up the run.
    return intent;
  }, [board, notify, session, macro]);

  /** Several intents at once (pasted rows): one batch, run in order (the runner queues them) or kept as drafts. */
  const createIntents = useCallback(async (texts: string[], run: boolean, positions: { dx: number; dy: number }[] = []) => {
    if (!board) throw new Error("No workstream is open.");
    const scope: Scope = { family: macro.family, group1: macro.group1, group2: macro.group2, skus: [] };
    const hint = macro.functions.length === 1 ? macro.functions[0] : "";
    const res = await api.createBatch(board.id, texts, scope, hint, run, "pasted", positions);
    setIntents((cur) => [...cur, ...res.intents]);
    notify(run ? `Running ${res.started} of ${res.intents.length} intents against “${session?.config.scenario}”. They queue, a couple at a time.`
               : `Added ${res.intents.length} intents as drafts. Run them from the batch header.`);
    return res.intents;
  }, [board, notify, session, macro]);

  const newBoard = async () => {
    const name = window.prompt("Name the new workstream", "Demand planning");
    if (!name) return;
    const created = await api.createBoard(name);
    setBoards((bs) => [...bs, { id: created.id, name: created.name }]);
    await loadBoard(created.id);
  };

  const workTypes = useMemo(
    () => Object.fromEntries((session?.taxonomy.work_types ?? []).map((w) => [w.id, w])),
    [session],
  );

  if (checking) return <div className="login-wrap"><div className="spinner" /></div>;
  if (!session) return <Login onSignedIn={loadSession} />;

  const { config, user } = session;

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand"><img src="/favicon.svg" alt="" /><span>gentiK</span></div>
        <nav className="tabs" role="tablist" aria-label="Views">
          <button className="tab" role="tab" aria-selected={view === "canvas"} onClick={() => setView("canvas")}>Canvas</button>
          <button className="tab" role="tab" aria-selected={view === "alerts"} onClick={() => setView("alerts")}>
            Alert Configs{attention > 0 && <span className="tab-badge" aria-label={`${attention} alerts need attention`}>{attention}</span>}
          </button>
          <button className="tab" role="tab" aria-selected={view === "dashboard"} onClick={() => setView("dashboard")}>Dashboard</button>
        </nav>
        {(view === "canvas" || view === "alerts") && boards.length > 0 && (
          <select
            className="input ws-select" aria-label="Workstream" title="Workstream" value={board?.id ?? ""}
            onChange={(e) => (e.target.value === "__new" ? newBoard() : loadBoard(e.target.value))}
          >
            {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            <option value="__new">+ New workstream…</option>
          </select>
        )}
        {view === "canvas" && board && (
          <button className="btn sm upload-btn" onClick={() => setUploading(true)} title="Replace a forecast category from an Excel or CSV file">
            <span aria-hidden>⇪</span><span className="upload-label">Upload</span>
          </button>
        )}
        {view === "canvas" && board && (
          <TopFilters value={macro} onChange={changeMacro} functions={session.taxonomy.functions}
                      intents={intents} workTypes={workTypes} />
        )}
        <div className="grow" />
        <div className="scenario-badge" title="Every action is written to this Kinaxis scenario">
          <span className="lbl muted">Target scenario</span>
          <b>{config.scenario}</b>
          {config.mode === "demo" && <span className="demo-flag" title="Synthetic data — not connected to Kinaxis">DEMO</span>}
        </div>
        {user.role === "admin" ? (
          // User management lives behind the admin's own name, not in the top menu.
          <button type="button" className="link-btn small ink2 user-name" title={`${user.username}: manage users`}
                  onClick={() => setView("users")}>{user.display_name}</button>
        ) : (
          <span className="small ink2 user-name" title={user.username}>{user.display_name}</span>
        )}
        <button className="btn ghost sm signout" onClick={async () => { await api.logout(); setSession(null); }}>Sign out</button>
      </header>

      <main className="main">
        {view === "canvas" && board && (
          <Canvas
            key={board.id}
            boardId={board.id}
            intents={intents}
            workTypes={workTypes}
            functions={session.taxonomy.functions}
            macro={macro}
            scenario={config.scenario}
            selectedIntent={openIntent}
            composeRequest={composeRequest}
            onOpenIntent={setOpenIntent}
            onMoveIntent={moveIntent}
            onCreateIntent={createIntent}
            onCreateIntents={createIntents}
            onRefresh={refreshIntents}
            alerts={alerts}
            onAlertsChanged={refreshAlerts}
            notify={notify}
          />
        )}
        {view === "alerts" && board && (
          <AlertConfigs key={board.id} boardId={board.id} boardName={board.name} macro={macro} notify={notify}
                        onAlertsChanged={refreshAlerts} />
        )}
        {view === "canvas" && board && openIntent && (
          <IntentDrawer
            key={openIntent}
            board={board}
            intentId={openIntent}
            workTypes={workTypes}
            functions={session.taxonomy.functions}
            scenario={config.scenario}
            onClose={() => setOpenIntent(null)}
            onChanged={refreshIntents}
            onDeleted={() => { setOpenIntent(null); refreshIntents(); }}
            onReuse={(it) => {
              setOpenIntent(null);
              setComposeRequest({ seed: { text: it.text, from: it.id }, nonce: Date.now() });
            }}
            notify={notify}
          />
        )}
        {view === "dashboard" && <Dashboard config={config} workTypes={workTypes} notify={notify} />}
        {view === "users" && user.role === "admin" && <Users me={user} notify={notify} />}
      </main>

      {uploading && board && (
        <Upload boardId={board.id} scenario={config.scenario} onClose={() => setUploading(false)}
                onStaged={(it) => {
                  setUploading(false);
                  refreshIntents();
                  setOpenIntent(null);
                  notify(`Staged: ${it.text}`);
                }} />
      )}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}
